<template>
    <case-conference-layout :patient="patient" v-if="loaded">
        <template #actionButtons>
            <div class="flex flex-col flex-shrink-0 space-y-3 sm:space-x-3 sm:space-y-0 sm:flex-row">
                <!-- Create -->
                <template v-if="!isView">
                    <button-component 
                        btnWidth="quinary"
                        fill="outline"
                        @click="router.replace(`/patient/${patientId}/cases/${caseId}/edit/`)"
                        customClass="quinary md:w-auto w-full"
                    >
                        Cancel
                    </button-component>
                    <button-component
                    @click="showConfirmation = showConfirmation = true"
                    customClass="primary md:w-auto w-full">
                        Save
                    </button-component>
                </template>

                <!-- View -->
                <template v-if="isView">
                    <button-component @click="isView = !isView" customClass="primary md:w-auto w-full">
                        <!-- <img
                            src="/assets/icon/ic-pencil-underline.svg"
                            alt="edit_icon"
                            class="object-contain w-4 h-4 mr-1 icon" /> -->
                        <pencil-underline-icon class="object-contain w-4 h-4 mr-1 icon" />
                        <span>Edit</span>
                    </button-component>
                    <button-component
                        v-if="isNetworkAvailable"
                        @click="generatePDFAccess()"
                        customClass="bg-green-600 hover:bg-green-700 text-white transition border border-green-600 hover:border-green-700 md:w-auto w-full"
                    >
                        <EyeIcon class="w-5 h-5 mr-2 -ml-1 icon" />
                        <span>View PDF</span>
                    </button-component>
                </template>

                <!-- Edit -->
                <template v-if="!createMode && editMode">
                    <button-component 
                        btnWidth="quinary"
                        fill="outline"
                        @click="editMode = !editMode"
                        customClass="quinary md:w-auto w-full"
                    >
                        Cancel
                    </button-component>
                    <button-component
                    @click="showConfirmation = showConfirmation = true"
                    customClass="primary md:w-auto w-full">
                        Save Changes
                    </button-component>
                </template>
                
            </div>
        </template>

        <factors-tab 
            :aggravatingFactors="aggravatingFactors"
            :mitigatingFactors="mitigatingFactors"
            :form="form"
            :isView="isView"
            @update:form="(value) => form = value"
        />

        <!-- MODAL -->
        <confirmation-modal
            :typeTwo="true"
            :show="showConfirmation"
            @cancel="showConfirmation = false"
            @confirm="save()"
            cancel-text="Cancel"
            action-text="Confirm"
            title="Save Changes"
            content="Are you sure do you want to save added details?" />
        <success-modal
            :show="showSuccess"
            @confirm="redirectSuccess()"
            action-text="Close"
            title="Success!"
            content="Details has been successfully saved!" />
        <page-loader :show="loading"/>
        <error-modal @close="closeError" :show="errors?.message ? true : false" :content="errors?.message" />
    </case-conference-layout>
</template>
<script setup lang="ts">
import PageLoader from "@/components/loaders/PageLoader.vue"
import CaseConferenceLayout from '../../Component/CaseConferenceLayout.vue';
import FactorsTab from "../../Tabs/FactorsTab.vue";
import ButtonComponent from "@/components/buttons/ButtonComponent.vue";
import ConfirmationModal from "@/components/modals/ConfirmationModal.vue";
import SuccessModal from "@/components/modals/SuccessModal.vue";
import { EyeIcon } from "@heroicons/vue/24/solid";

import { ref, onMounted, computed, watch } from 'vue';
import { useRouter, useRoute } from "vue-router";
import ErrorModal from "@/components/modals/ErrorModal.vue";
import PersonalInformationService from "@/classes/Cases/IntakeForms/PersonalInformationService";
import CaseConferenceService from "@/classes/Cases/CaseConference/CaseConferenceService";
import { errorResponse } from "@/helpers/errorMessage";
import FormOptions from "@/classes/Forms/FormOptions";
import { useNetworkStore } from '@/store/network';
import OfflineSyncCaseConference from "@/classes/Forms/Cases/CaseConference/OfflineSyncCaseConference";
import OfflineSyncAggrigatingFactor from "@/classes/Forms/Cases/CaseConference/OfflineSyncAggrigatingFactor";
import { onIonViewWillEnter } from "@ionic/vue";
import PencilUnderlineIcon from "@/components/icons/pencilUnderlineIcon.vue";
import OnlineSynching from "@/classes/Synching/OnlineSynching";

const router = useRouter();
const route = useRoute();
const loading = ref(false);
const loaded = ref(false);
const showConfirmation = ref<boolean>(false);
const showSuccess = ref<boolean>(false);
const createMode = ref<boolean>(true);
const editMode = ref<boolean>(true);
const isView = ref(false)

const route_params = route.params;
const errors = ref<any>();
const patient = ref<string>('');
const patientId = ref<number | null>(route.params.patient);
const caseId = ref<number | null>(route.params.case);
const isIonWillEnter = ref<boolean>(false);
const caseConferenceId = ref<number | null>(route.params.caseConference);

const aggravatingFactors = ref([])
const mitigatingFactors = ref([])
const item = ref(null)
const form = ref({
    id: null,
    aggravatingFactors: [],
    mitigatingFactors: [],
    other_aggravating: null,
    other_mitigating: null,
    case_conference_id: null
})

const networkStore = useNetworkStore();
const isNetworkAvailable = computed(() => networkStore.isNetworkAvailable);
const options = new FormOptions()
const onlineSync = new OnlineSynching();

const fetch = async ():void => {
    loading.value = true;
    if (isNetworkAvailable.value) {
        await PersonalInformationService
        .show(route_params?.patient, route_params?.case)
        .then(({data}) => {
            patient.value = data.patient_name;

            loading.value = false;
            loaded.value = true;
        })
        .catch(errors => {
            loading.value = false;
            console.log(errors)
        }) 
    } else {
        patient.value = await OfflineSyncCaseConference.renderPatientName(route_params?.case, true);
        loading.value = false;
        loaded.value = true;
    }

}

const fetchFactors = async (): Promise<any> => {
    loading.value = true;

    await CaseConferenceService
        .indexFactors(
            patientId.value,
            caseId.value,
            caseConferenceId.value,
            isNetworkAvailable.value
        )
        .then(async ({data}) => {
            // isView.value = true
            
            aggravatingFactors.value = data.aggravatingFactors
            mitigatingFactors.value = data.mitigatingFactors

            Object.assign(form.value, data.item);

            if (form.value.aggravatingFactors.length > 0 && form.value.mitigatingFactors.length > 0) {
                isView.value = true
            }

            await storeOffline(data.item)
        })
        .catch((error: object) => { 
            console.log(error)
            errors.value = errorResponse(error.response?.data?.message);
        })
        .finally(() => loading.value = false);
	
}

const storeOffline = async (item:any): Promise<void> =>{
    if(isNetworkAvailable.value){
        await CaseConferenceService.storeFactorsOffline(item);             
    }
}

const save = async () => {
    loading.value = true
    showConfirmation.value = false
    await CaseConferenceService.storeFactors(
        patientId.value,
        caseId.value,
        caseConferenceId.value,
        form.value,
        isNetworkAvailable.value
    )
        .then(async (response: object) => {
            await fetchFactors();
            isView.value = true
            showSuccess.value = true;
        })
        .catch((error: object) => { 
            console.log(error)
            showConfirmation.value = false
            errors.value = errorResponse(error.response?.data?.message);
        })
        .finally(() => loading.value = false);

}

const closeError = () => {
    errors.value = ''
}

const generatePDFAccess = ():void => {
	CaseConferenceService
		.generatePDFAccess(
			patientId.value,
			caseId.value,
			form.value.case_conference_id,
            isNetworkAvailable.value
		)
		.then(({data}) => {
			if(data.token){
				downloadPDF(data.token)
			}
		}).catch((errors) => {
			console.log(errors);
		})

}
const downloadPDF = (token:string):void => {
	CaseConferenceService
		.downloadPDF(
			form.value.case_conference_id,
			token,
            isNetworkAvailable.value
		)
		.then(({data}) => {
			console.log(data);
		}).catch((errors) => {
			console.log(errors);
		})

}

const redirectSuccess = (): void => {
    showSuccess.value = false;
    createMode.value = false;
    editMode.value = false;

    // router.go(0);
};

// watch(
// 	() => isNetworkAvailable.value,
// 	async (val) => {
// 		console.log("Watcher - Network: " + val);
//         await fetchFactors();
// 	}
// );

onIonViewWillEnter(async () => {
    if (isIonWillEnter.value) {
        await fetch();
        await fetchFactors();
        await onlineSync.checkDrafts();
    }
});

onMounted(async () => {
    await fetch();    
    await fetchFactors();
    isIonWillEnter.value = true;
})

</script>

